import { useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input } from "./Input";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /**
   * Si fourni, l'utilisateur doit taper EXACTEMENT cette phrase pour
   * débloquer le bouton de confirmation (action très sensible).
   */
  requireType?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
  busy?: boolean;
};

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  requireType,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  destructive = false,
  onConfirm,
  busy = false,
}: Props) {
  const [typed, setTyped] = useState("");
  const canConfirm = requireType ? typed === requireType : true;

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        {description ? (
          <div className="text-sm text-navy-700">{description}</div>
        ) : null}

        {requireType ? (
          <div className="rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
              Pour confirmer, saisir exactement :
            </p>
            <code className="block rounded-lg bg-white px-3 py-2 text-xs font-mono text-navy-800 ring-1 ring-amber-200 break-all">
              {requireType}
            </code>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              placeholder="Tapez la phrase exacte ici"
            />
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? "danger" : "primary"}
            onClick={() => {
              if (!canConfirm) return;
              void onConfirm();
            }}
            disabled={!canConfirm || busy}
          >
            {busy ? "…" : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
